import { NavLink, Outlet } from "react-router-dom";
import { useDashboardData } from "../context/DashboardDataContext";
import { useUiPreferences } from "../context/UiPreferencesContext";
import { PATTERN_LABELS } from "../dashboard/analytics";

function escapeCsvField(val) {
  if (val == null || val === "") return "";
  const s = String(val);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const NAV = [
  { to: "/", label: "Executive Overview", end: true, icon: "grid" },
  { to: "/patterns", label: "Pattern Intelligence", icon: "layers" },
  { to: "/funnel", label: "Assessment Funnel", icon: "funnel" },
  { to: "/conversion", label: "Conversion & Revenue", icon: "trend" },
  { to: "/cohorts", label: "Cohorts & Trends", icon: "users" },
  { to: "/submissions", label: "Submissions Explorer", icon: "table" },
  { to: "/founder", label: "Founder Profile", icon: "user" },
  { to: "/settings", label: "Settings", icon: "gear" },
];

function NavIcon({ name }) {
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.75 };
  switch (name) {
    case "grid":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "layers":
      return (
        <svg {...common}>
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      );
    case "funnel":
      return (
        <svg {...common}>
          <path d="M4 4h16l-6 8v6l-4 2v-8L4 4z" />
        </svg>
      );
    case "trend":
      return (
        <svg {...common}>
          <path d="M3 17l6-6 4 4 8-8" />
          <path d="M14 7h7v7" />
        </svg>
      );
    case "users":
      return (
        <svg {...common}>
          <circle cx="9" cy="7" r="4" />
          <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
          <circle cx="17" cy="11" r="3" />
          <path d="M21 21v-1a3 3 0 00-2-2.8" />
        </svg>
      );
    case "table":
      return (
        <svg {...common}>
          <path d="M3 6h18v12H3zM3 12h18M9 6v12" />
        </svg>
      );
    case "user":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20v-2a4 4 0 014-4h8a4 4 0 014 4v2" />
        </svg>
      );
    case "gear":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
        </svg>
      );
    default:
      return null;
  }
}

export function DashboardLayout() {
  const { density, exportFormat } = useUiPreferences();
  const {
    loading,
    error,
    timeframe,
    setTimeframe,
    revenue,
    setRevenue,
    revenueOptions,
    reload,
    submissions,
  } = useDashboardData();

  function handleExport() {
    if (exportFormat === "csv") {
      const headers = [
        "id",
        "full_name",
        "email",
        "created_at",
        "primary",
        "overall_percent",
        "revenue",
      ];
      const lines = [headers.join(",")];
      submissions.forEach((s) => {
        lines.push(
          [
            escapeCsvField(s.id),
            escapeCsvField(s.full_name || s.name),
            escapeCsvField(s.email),
            escapeCsvField(s.createdAt),
            escapeCsvField(PATTERN_LABELS[s.primary] || s.primary),
            escapeCsvField(s.overallPercent),
            escapeCsvField(s.revenue),
          ].join(",")
        );
      });
      const blob = new Blob(["\uFEFF" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `founder-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }
    window.print();
  }

  return (
    <div className="app-shell" data-density={density}>
      <aside className="app-shell__sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-brand__mark" aria-hidden />
          <div>
            <div className="sidebar-brand__title">Executive Intelligence</div>
            <div className="sidebar-brand__sub">Sovereign Analyst</div>
          </div>
        </div>
        <nav className="sidebar-nav" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-nav__link${isActive ? " is-active" : ""}`
              }
            >
              <span className="sidebar-nav__icon">
                <NavIcon name={item.icon} />
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="status-pill">
            <span className="status-pill__dot" />
            System operational
          </div>
        </div>
      </aside>

      <div className="app-shell__main">
        <header className="top-bar">
          <div className="top-bar__product">
            <span className="top-bar__eyebrow">Product</span>
            <h2 className="top-bar__name">Founder Decision Debt Intelligence</h2>
          </div>
          <div className="top-bar__filters">
            <label className="field-inline">
              <span>Timeframe</span>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                disabled={loading}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="all">All time</option>
              </select>
            </label>
            <label className="field-inline">
              <span>Revenue</span>
              <select
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                disabled={loading}
              >
                {revenueOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "all" ? "All ranges" : opt}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn-icon" title="Refresh" onClick={reload}>
              ↻
            </button>
            <button type="button" className="btn-primary" onClick={handleExport}>
              Export {exportFormat === "csv" ? "(CSV)" : "(PDF)"}
            </button>
          </div>
        </header>

        {error && (
          <div className="banner banner--error" role="alert">
            {error}
          </div>
        )}

        <div className="app-shell__content">
          {loading ? (
            <div className="state state--loading">
              <div className="spinner" aria-hidden />
              <p>Loading intelligence layer…</p>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}
